import { useState } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Button from '../../common/Button';
import { useToast } from '../../../hooks/useToast';

// 플랫폼별 콘텐츠 포맷 매핑 (백엔드 Platform enum 값 기준)
export const PLATFORM_CONTENT_FORMATS = {
  Instagram: ['IG Pics', 'IG Reels'],
  Facebook: ['FB'],
  Youtube: ['YT Exclusive', 'YT Non-Exclusive', 'YT Shorts'],
  Tiktok: ['TT VDO', 'TT Pics'],
  X: ['X Feed'],
  XHS: ['XHS Feed'],
  Lemon8: ['Lemon8 Feed']
};

// 플랫폼별 기본 URL prefix (백엔드 Platform enum 값 기준)
const PLATFORM_URL_PREFIX = {
  Instagram: 'instagram.com/',
  Facebook: 'facebook.com/',
  Youtube: 'youtube.com/@',
  Tiktok: 'tiktok.com/@',
  X: 'x.com/',
  XHS: 'www.xiaohongshu.com/user/profile/',
  Lemon8: 'www.lemon8-app.com/'
};

// URL에서 계정명 추출 함수
const extractUsername = (platform, url) => {
  if (!url) return '';
  const prefix = PLATFORM_URL_PREFIX[platform];
  if (!prefix) return '';

  // prefix 이후의 부분 추출
  if (url.startsWith('https://')) {
    url = url.replace('https://', '');
  }
  if (url.startsWith('http://')) {
    url = url.replace('http://', '');
  }

  if (url.startsWith(prefix)) {
    return url.substring(prefix.length);
  }

  return '';
};

// 국가 선택지 (등록 폼 select와 리서치 모달의 "배치 기본 국가" selector가 공유)
export const COUNTRY_OPTIONS = [
  'Vietnam', 'Thailand', 'Korea', 'US', 'Japan', 'Taiwan',
  'HongKong', 'China', 'Singapore', 'Indonesia', 'Malaysia', 'Philippine',
];

// 팔로워 수로 규모 유형 계산
const calculateAdType = (followers) => {
  if (!followers || followers.trim() === '') return '자동 분류';
  const count = parseInt(followers.replace(/,/g, ''), 10);
  if (isNaN(count) || count === 0) return '자동 분류';
  if (count <= 9999) return 'Nano';
  if (count <= 99999) return 'Micro';
  if (count <= 999999) return 'Macro';
  return 'Mega';
};

function InfluencerRegisterModal({ isOpen, onClose, onRegister }) {
  const { showError } = useToast();
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false); // 프로필 URL 중복 에러 상태
  const [nicknameError, setNicknameError] = useState(false); // 대표 닉네임 중복 에러 상태
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    uploadType: '플랫폼 선택',
    channels: [],
    contacts: [{ channel: '컨택 채널', idOrEmail: '' }],
    representativeName: '',
    country: '',
    // 추가 정보
    lastName: '',
    firstName: '',
    fullName: '',
    idNo: '',
    phone: '',
    deliveryRecipientName: '',
    deliveryAddress: '',
    deliveryPostalCode: '',
    bankName: '',
    accountNumber: '',
    bankAddress: ''
  });

  // 채널 추가
  const handleAddChannel = () => {
    if (formData.uploadType === '플랫폼 선택') {
      showError('운영 채널을 먼저 선택해주세요');
      return;
    }

    const platform = formData.uploadType;
    const defaultUrl = PLATFORM_URL_PREFIX[platform] || '';

    const newChannel = {
      platform: platform,
      profileUrl: defaultUrl,
      contentFormats: [],
      followers: '',
      adType: '자동 분류',
      memo: ''
    };

    const newChannels = [...formData.channels, newChannel];

    // 첫 번째 채널 추가 시 대표 닉네임을 빈 문자열로 초기화
    // (사용자가 URL을 입력하면 자동으로 채워짐)
    const updatedRepresentativeName = newChannels.length === 1 ? '' : formData.representativeName;

    setFormData({
      ...formData,
      channels: newChannels,
      representativeName: updatedRepresentativeName,
      uploadType: '플랫폼 선택' // 선택 초기화
    });
  };

  // 채널 삭제
  const handleRemoveChannel = (index) => {
    const newChannels = formData.channels.filter((_, i) => i !== index);

    // 첫 번째 채널을 삭제한 경우, 새로운 첫 번째 채널의 URL에서 대표 닉네임 추출
    let updatedRepresentativeName = formData.representativeName;
    if (index === 0 && newChannels.length > 0) {
      const username = extractUsername(newChannels[0].platform, newChannels[0].profileUrl);
      if (username) {
        updatedRepresentativeName = username;
      }
    } else if (newChannels.length === 0) {
      // 모든 채널이 삭제된 경우 대표 닉네임도 초기화
      updatedRepresentativeName = '';
    }

    setFormData({
      ...formData,
      channels: newChannels,
      representativeName: updatedRepresentativeName
    });
  };

  // 채널 정보 변경
  const handleChannelChange = (index, field, value) => {
    const newChannels = [...formData.channels];
    newChannels[index][field] = value;

    // 팔로워 수 변경 시 규모 유형 자동 계산
    if (field === 'followers') {
      newChannels[index].adType = calculateAdType(value);
    }

    // 첫 번째 채널의 프로필 URL 변경 시 대표 닉네임 자동 업데이트
    let updatedRepresentativeName = formData.representativeName;
    if (field === 'profileUrl' && index === 0) {
      const username = extractUsername(newChannels[0].platform, value);
      if (username) {
        updatedRepresentativeName = username;
      }
    }

    setFormData({
      ...formData,
      channels: newChannels,
      representativeName: updatedRepresentativeName
    });
  };

  // 채널의 콘텐츠 포맷 체크박스 토글
  const handleContentFormatToggle = (channelIndex, format) => {
    const newChannels = [...formData.channels];
    const currentFormats = newChannels[channelIndex].contentFormats;

    if (currentFormats.includes(format)) {
      newChannels[channelIndex].contentFormats = currentFormats.filter(f => f !== format);
    } else {
      newChannels[channelIndex].contentFormats = [...currentFormats, format];
    }

    setFormData({ ...formData, channels: newChannels });
  };

  const handleAddContact = () => {
    setFormData({
      ...formData,
      contacts: [...formData.contacts, { channel: '컨택 채널', idOrEmail: '' }]
    });
  };

  const handleRemoveContact = (index) => {
    const newContacts = formData.contacts.filter((_, i) => i !== index);
    setFormData({ ...formData, contacts: newContacts });
  };

  const handleContactChange = (index, field, value) => {
    const newContacts = [...formData.contacts];
    newContacts[index][field] = value;
    setFormData({ ...formData, contacts: newContacts });
  };

  const handleSubmit = async () => {
    // 필수 필드 검증
    const newErrors = {};

    if (formData.channels.length === 0) {
      newErrors.channels = true;
      showError('최소 1개 이상의 채널을 추가해주세요');
      setFieldErrors(newErrors);
      return;
    }

    // 각 채널의 프로필 URL 검증
    const channelWithoutUrl = formData.channels.find(
      channel => !channel.profileUrl || channel.profileUrl.trim() === '' || channel.profileUrl === `${channel.platform.toLowerCase()}.com/`
    );
    if (channelWithoutUrl) {
      newErrors.profileUrl = true;
      showError('프로필 URL을 입력해주세요');
      setFieldErrors(newErrors);
      return;
    }

    // 각 채널의 활동 콘텐츠 검증
    const channelWithoutContent = formData.channels.find(
      channel => channel.contentFormats.length === 0
    );
    if (channelWithoutContent) {
      newErrors.contentFormats = true;
      showError('콘텐츠를 1개 이상 등록해주세요');
      setFieldErrors(newErrors);
      return;
    }

    if (!formData.representativeName || formData.representativeName.trim() === '') {
      newErrors.representativeName = true;
      showError('대표 닉네임을 입력해주세요');
      setFieldErrors(newErrors);
      return;
    }

    if (!formData.country || formData.country === '') {
      newErrors.country = true;
      showError('국가를 선택해주세요');
      setFieldErrors(newErrors);
      return;
    }

    setFieldErrors({});

    // onRegister가 성공했을 때만 리셋
    try {
      setDuplicateError(false); // 프로필 URL 에러 상태 초기화
      setNicknameError(false); // 대표 닉네임 에러 상태 초기화
      await onRegister(formData);
      handleReset();
    } catch (error) {
      // 에러 발생 시 모달 유지 (리셋하지 않음)
      console.error('Registration failed:', error);

      // 중복 에러 처리
      const errorData = error.response?.data;
      const errorMessage = errorData?.error?.message || errorData?.message || error.message;
      if (errorMessage?.includes('프로필 URL 중복')) {
        setDuplicateError(true);
      } else if (errorMessage?.includes('already in use') || errorMessage?.includes('대표 닉네임 중복')) {
        setNicknameError(true);
      }
    }
  };

  const handleReset = () => {
    setFormData({
      uploadType: '플랫폼 선택',
      channels: [],
      contacts: [{ channel: '컨택 채널', idOrEmail: '' }],
      representativeName: '',
      country: '',
      // 추가 정보 초기화
      lastName: '',
      firstName: '',
      fullName: '',
      idNo: '',
      phone: '',
      deliveryRecipientName: '',
      deliveryAddress: '',
      deliveryPostalCode: '',
      bankName: '',
      accountNumber: '',
      bankAddress: ''
    });
    setShowAdditionalInfo(false);
    setDuplicateError(false);
    setNicknameError(false);
    setFieldErrors({});
  };

  const handleAdditionalInfo = () => {
    setShowAdditionalInfo(true);
  };

  const handleAdditionalInfoCancel = () => {
    setShowAdditionalInfo(false);
  };

  const handleAdditionalInfoComplete = () => {
    setShowAdditionalInfo(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="인플루언서 등록" size="md">
      {showAdditionalInfo ? (
        // 추가 정보 입력 폼
        <div className="space-y-4">
          {/* 성, 이름, 본명 */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-dark mb-2">
                성(Last name)
              </label>
              <Input
                placeholder="Last name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-dark mb-2">
                이름(First name)
              </label>
              <Input
                placeholder="First name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-dark mb-2">
                본명(Fullname)
              </label>
              <Input
                placeholder="Fullname"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              />
            </div>
          </div>

          {/* ID No. */}
          <div>
            <label className="block text-xs font-medium text-text-dark mb-2">
              ID No.
            </label>
            <Input
              placeholder="******_*******"
              value={formData.idNo}
              onChange={(e) => setFormData({ ...formData, idNo: e.target.value })}
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-medium text-text-dark mb-2">
              Phone
            </label>
            <Input
              placeholder="010-****-****"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>

          {/* Delivery Recipient Name */}
          <div>
            <label className="block text-xs font-medium text-text-dark mb-2">
              Delivery Recipient Name
            </label>
            <Input
              placeholder="Name"
              value={formData.deliveryRecipientName}
              onChange={(e) => setFormData({ ...formData, deliveryRecipientName: e.target.value })}
            />
          </div>

          {/* Delivery Address */}
          <div>
            <label className="block text-xs font-medium text-text-dark mb-2">
              Delivery Address
            </label>
            <Input
              placeholder="Address"
              value={formData.deliveryAddress}
              onChange={(e) => setFormData({ ...formData, deliveryAddress: e.target.value })}
            />
          </div>

          {/* Delivery Postal Code */}
          <div>
            <label className="block text-xs font-medium text-text-dark mb-2">
              Delivery Postal Code
            </label>
            <Input
              placeholder="13212"
              value={formData.deliveryPostalCode}
              onChange={(e) => setFormData({ ...formData, deliveryPostalCode: e.target.value })}
            />
          </div>

          {/* Bank Name */}
          <div>
            <label className="block text-xs font-medium text-text-dark mb-2">
              Bank Name
            </label>
            <Input
              placeholder="우리 은행"
              value={formData.bankName}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
            />
          </div>

          {/* Account Number */}
          <div>
            <label className="block text-xs font-medium text-text-dark mb-2">
              Account Number
            </label>
            <Input
              placeholder="****-*****-*****"
              value={formData.accountNumber}
              onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
            />
          </div>

          {/* Bank's address */}
          <div>
            <label className="block text-xs font-medium text-text-dark mb-2">
              Bank's address
            </label>
            <Input
              placeholder="****-***-*******"
              value={formData.bankAddress}
              onChange={(e) => setFormData({ ...formData, bankAddress: e.target.value })}
            />
          </div>

          {/* 하단 버튼 */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              className="flex-1 !text-xs"
              onClick={handleAdditionalInfoCancel}
            >
              취소
            </Button>
            <Button
              variant="black"
              className="flex-1 !text-xs"
              onClick={handleAdditionalInfoComplete}
            >
              완료
            </Button>
          </div>
        </div>
      ) : (
        // 기본 폼
        <div className="space-y-6">
        {/* 운영 채널 */}
        <div className="flex items-center gap-4 justify-between">
          <label className="text-xs font-medium text-text-dark w-24">
            운영 채널*
          </label>
          <div>
            <select
              value={formData.uploadType}
              onChange={(e) => setFormData({ ...formData, uploadType: e.target.value })}
              className="w-48 px-3 py-2 border border-border-gray rounded-lg text-xs mr-2"
            >
              <option value="플랫폼 선택">플랫폼 선택</option>
              <option value="Instagram">Instagram</option>
              <option value="Facebook">Facebook</option>
              <option value="Youtube">Youtube</option>
              <option value="Tiktok">Tiktok</option>
              <option value="X">X</option>
              <option value="XHS">XHS</option>
              <option value="Lemon8">Lemon8</option>
            </select>
            <Button
              variant="black"
              className="!w-auto px-6 !text-xs"
              onClick={handleAddChannel}
              disabled={formData.uploadType === '플랫폼 선택'}
            >
              채널 추가
            </Button>
          </div>
          
        </div>

        {/* 추가된 채널들 */}
        {formData.channels.map((channel, index) => (
          <div key={index} className="border border-border-gray rounded-lg p-4 space-y-4">
            {/* 채널 선택 */}
            <div className="flex items-center gap-4">
              <label className="text-xs font-medium text-text-dark w-20">
                채널*
              </label>
              <select
                value={channel.platform}
                onChange={(e) => handleChannelChange(index, 'platform', e.target.value)}
                className="flex-1 px-3 py-2 border border-border-gray rounded-lg text-xs bg-gray-50"
                disabled
              >
                <option value="Instagram">Instagram</option>
                <option value="Facebook">Facebook</option>
                <option value="Youtube">Youtube</option>
                <option value="Tiktok">Tiktok</option>
                <option value="X">X</option>
                <option value="XHS">XHS</option>
                <option value="Lemon8">Lemon8</option>
              </select>
            </div>

            {/* 프로필 URL */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-4">
                <label className="text-xs font-medium text-text-dark w-20">
                  프로필 URL*
                </label>
                <Input
                  placeholder="username"
                  prefix={PLATFORM_URL_PREFIX[channel.platform] || ''}
                  value={extractUsername(channel.platform, channel.profileUrl)}
                  onChange={(e) => {
                    const prefix = PLATFORM_URL_PREFIX[channel.platform] || '';
                    const fullUrl = prefix + e.target.value;
                    handleChannelChange(index, 'profileUrl', fullUrl);
                    setDuplicateError(false); // URL 변경 시 에러 초기화
                  }}
                  className="flex-1"
                />
              </div>
              {duplicateError && (
                <p className="text-red-500 text-xs ml-24">
                  해당 ID가 이미 등록됐습니다. 중복된 플랫폼 계정인지 확인해주세요
                </p>
              )}
            </div>

            {/* 활동 콘텐츠 */}
            <div className="flex items-start gap-4">
              <label className="text-xs font-medium text-text-dark w-20 mt-2">
                활동 콘텐츠*
              </label>
              <div className="flex gap-2 flex-wrap">
                {(PLATFORM_CONTENT_FORMATS[channel.platform] || []).map((format) => (
                  <label key={format} className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={channel.contentFormats.includes(format)}
                      onChange={() => handleContentFormatToggle(index, format)}
                      className="checkbox-default"
                    />
                    <span className="text-xs">{format}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 팔로워 수 */}
            <div className="flex items-center gap-4">
              <label className="text-xs font-medium text-text-dark w-20">팔로워 수</label>
              <Input
                placeholder="100"
                value={channel.followers}
                onChange={(e) => handleChannelChange(index, 'followers', e.target.value)}
                className="w-40"
              />
              <label className="text-xs font-medium text-text-dark">규모 유형</label>
              <select
                value={channel.adType}
                onChange={(e) => handleChannelChange(index, 'adType', e.target.value)}
                className="flex-1 px-3 py-2 border border-border-gray rounded-lg text-xs bg-gray-50"
                disabled
              >
                <option value="자동 분류">자동 분류</option>
                <option value="Nano">Nano</option>
                <option value="Micro">Micro</option>
                <option value="Macro">Macro</option>
                <option value="Mega">Mega</option>
              </select>
            </div>

            {/* 메모 */}
            <div className="flex items-start gap-4">
              <label className="text-xs font-medium text-text-dark w-20 mt-2">
                메모<br />(BLB내부용)
              </label>
              <textarea
                placeholder="BLB 내부용 메모"
                value={channel.memo}
                onChange={(e) => handleChannelChange(index, 'memo', e.target.value)}
                className="flex-1 px-3 py-2 border border-border-gray rounded-lg text-xs resize-none"
                rows={3}
              />
            </div>

            {/* 삭제 버튼 */}
            <div className="flex justify-end">
              <button
                onClick={() => handleRemoveChannel(index)}
                className="px-4 py-2 text-red-500 border border-red-500 rounded-lg text-xs hover:bg-red-50"
              >
                삭제
              </button>
            </div>
          </div>
        ))}

        {/* 컨택 수단 */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-text-dark">컨택 수단</label>
            <Button
              variant="black"
              className="!w-auto px-6 !text-xs"
              onClick={handleAddContact}
            >
              컨택 추가
            </Button>
          </div>

          <div className="space-y-2">
            {formData.contacts.map((contact, index) => (
              <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-md">
                <select
                  value={contact.channel}
                  onChange={(e) => handleContactChange(index, 'channel', e.target.value)}
                  className="w-40 px-3 py-2 border border-border-gray rounded-lg text-xs bg-white"
                >
                  <option value="컨택 채널">컨택 채널</option>
                  <option value="Line">Line</option>
                  <option value="Zalo">Zalo</option>
                  <option value="Kakao">Kakao</option>
                  <option value="TT">TT</option>
                  <option value="IG">IG</option>
                  <option value="Whatsapp">Whatsapp</option>
                  <option value="phone">phone</option>
                  <option value="email">email</option>
                  <option value="Others">Others</option>
                </select>
                <Input
                  placeholder="아이디 or 이메일 입력"
                  value={contact.idOrEmail}
                  onChange={(e) => handleContactChange(index, 'idOrEmail', e.target.value)}
                  className="flex-1"
                />
                <button
                  onClick={() => handleRemoveContact(index)}
                  className="px-3 py-2 text-red-500 border border-red-500 rounded-lg text-xs hover:bg-red-50 bg-white"
                >
                  삭제
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* 대표 닉네임 */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-4">
            <label className="text-xs font-medium text-text-dark w-24">
              대표 닉네임*
            </label>
            <Input
              placeholder="blbent"
              value={formData.representativeName}
              onChange={(e) => {
                setFormData({ ...formData, representativeName: e.target.value });
                setNicknameError(false);
                if (fieldErrors.representativeName) setFieldErrors(prev => ({ ...prev, representativeName: false }));
              }}
              className="flex-1"
              variant={fieldErrors.representativeName ? 'error' : 'default'}
            />
          </div>
          {nicknameError && (
            <p className="text-red-500 text-xs ml-28">
              해당 대표 닉네임이 이미 등록됐습니다. 중복된 대표 닉네임인지 확인해주세요
            </p>
          )}
        </div>

        {/* 국가 */}
        <div className="flex items-center gap-4">
          <label className="text-xs font-medium text-text-dark w-24">
            국가*
          </label>
          <select
            value={formData.country}
            onChange={(e) => {
              setFormData({ ...formData, country: e.target.value });
              if (fieldErrors.country) setFieldErrors(prev => ({ ...prev, country: false }));
            }}
            className={`flex-1 px-3 py-2 border rounded-lg text-xs ${fieldErrors.country ? 'border-red-500' : 'border-border-gray'}`}
          >
            <option value="">선택</option>
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* 하단 버튼 */}
        <div className="flex gap-3 pt-4">
          <Button
            variant="outline"
            className="flex-1 !text-xs"
            onClick={() => {
              handleReset();
              onClose();
            }}
          >
            취소
          </Button>
          <Button
            variant="outline"
            className="flex-1 !text-xs"
            onClick={handleAdditionalInfo}
          >
            추가 정보 입력
          </Button>
          <Button
            variant="black"
            className="flex-1 !text-xs"
            onClick={handleSubmit}
          >
            {formData.channels.length === 0
              ? '채널을 1개 이상 등록해주세요'
              : formData.channels.some(channel => !channel.profileUrl || channel.profileUrl.trim() === '' || channel.profileUrl === `${channel.platform.toLowerCase()}.com/`)
              ? '프로필 URL을 입력해주세요'
              : formData.channels.some(channel => channel.contentFormats.length === 0)
              ? '콘텐츠를 1개 이상 등록해주세요'
              : !formData.representativeName || formData.representativeName.trim() === ''
              ? '대표 닉네임을 입력해주세요'
              : !formData.country || formData.country === ''
              ? '국가를 선택해주세요'
              : '등록하기'}
          </Button>
        </div>
      </div>
      )}
    </Modal>
  );
}

export default InfluencerRegisterModal;
